import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useListWorkspacesInfiniteQuery } from '@frontend/api-workspaces';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useSelectedScope } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { NakedButton, Text } from '@frontend/design-system';
import { useWorkspaceState } from '../../hooks/use-workspace-state';
import { useWorkspaceShallowStore } from '../../store';
import type { Workspace } from '../../types';
import { WorkspaceItem } from './workspace-item';
import { WorkspaceItemSkeleton } from './workspace-item-skeleton';

type Props = {
  orgId: string;
  accessibleLocationIds: string[];
  selectedLocationIds: string[];
  onSelectWorkspace: (locationIds: string[]) => void;
};

// Memoized wrapper to prevent re-renders when parent re-renders with same props.
// Accessibility is computed at the parent level (GlsWorkspaceSection) to avoid
// per-item scope store subscriptions that will cause re-renders on heavy pages.
const MemoizedWorkspaceItem = memo(function MemoizedWorkspaceItem({
  workspace,
  accessibleCount,
  isSelected,
  disabled,
  onSelect,
}: {
  workspace: Workspace;
  accessibleCount: number;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <WorkspaceItem
      workspace={workspace}
      accessibleCount={accessibleCount}
      totalLocationCount={workspace.locationIds.length}
      isSelected={isSelected}
      onClick={onSelect}
      disabled={disabled}
    />
  );
});

const areSameLocationSet = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false;
  }
  const leftSet = new Set(left);
  const rightSet = new Set(right);
  if (leftSet.size !== rightSet.size) {
    return false;
  }
  return [...leftSet].every((id) => rightSet.has(id));
};

export function GlsWorkspaceSection({ orgId, accessibleLocationIds, selectedLocationIds, onSelectWorkspace }: Props) {
  const { t } = useTranslation('settings');
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const currentSelectedScope = useSelectedScope();
  const { selectedWorkspaceId, pendingWorkspaceId, setPendingWorkspaceId } = useWorkspaceShallowStore(
    'selectedWorkspaceId',
    'pendingWorkspaceId',
    'setPendingWorkspaceId'
  );
  // Compute isWorkspaceAdmin from the already-available accessibleLocationIds prop.
  // This avoids subscribing to accessibleLocationIds via useAppScopeSelection()
  // (which was the expensive part of useWorkspaceFeature()) inside the GLS render path.
  const isWorkspaceAdmin = useMemo(
    () => accessibleLocationIds.some((id) => hasSchemaACL(id, Permission.WORKSPACES_MANAGE)),
    [accessibleLocationIds]
  );

  const { isWorkspacesLoading, isWorkspacesEnabled } = useWorkspaceState(orgId);
  const workspacesQuery = useListWorkspacesInfiniteQuery(orgId, {
    enabled: !!isWorkspacesEnabled,
  });

  const workspaces = useMemo(
    () =>
      (workspacesQuery.data?.pages.flatMap((p) => p.workspaces ?? []) ?? []).flatMap((raw) => {
        if (!raw.workspaceId || !raw.orgId || !raw.workspaceName) return [];
        return [
          {
            workspaceId: raw.workspaceId,
            orgId: raw.orgId,
            workspaceName: raw.workspaceName,
            locationIds: raw.locationIds ?? [],
            userIds: raw.userIds ?? [],
            updateTime: raw.updateTime ?? '',
            updaterId: raw.updaterId ?? '',
          } satisfies Workspace,
        ];
      }),
    [workspacesQuery.data]
  );
  const sentinelRef = useRef<HTMLDivElement>(null);
  const accessibleLocationSet = useMemo(() => new Set(accessibleLocationIds), [accessibleLocationIds]);
  const workspaceRows = useMemo(
    () =>
      workspaces.map((workspace) => {
        const intersectedLocationIds = workspace.locationIds.filter((id) => accessibleLocationSet.has(id));
        // Compute accessibility here (parent level) instead of per-item hook.
        // A workspace is accessible when the user has scope access to ALL its locations.
        // This is the same logic as useIsWorkspaceAccessible but avoids N scope store
        // subscriptions that cause re-render storms on heavy pages like calls/messaging.
        const isAccessible = intersectedLocationIds.length === workspace.locationIds.length;
        return {
          workspace,
          intersectedLocationIds,
          accessibleCount: intersectedLocationIds.length,
          isAccessible,
        };
      }),
    [workspaces, accessibleLocationSet]
  );

  // Show loading when hook says we're loading OR when workspaces exist but waiting for location data
  const shouldShowLoading = isWorkspacesLoading || (workspaces.length > 0 && workspaceRows.length === 0);

  const previousOrgId = useRef<string | null>(null);
  const hasSeededFromScope = useRef(false);

  // Initialise the pending highlight from the committed workspace when the picker opens.
  // Uses a ref flag because selectedScope starts undefined (async init from latestSelectedScope)
  // and may only become defined after mount, so we wait for the first non-undefined value.
  // ONLY runs once during initialization - doesn't run when user clicks workspace.
  useEffect(() => {
    if (hasSeededFromScope.current) return;
    if (currentSelectedScope === undefined) {
      return; // scope not yet initialized
    }
    // Only initialize if we don't already have a pending workspace selection
    if (pendingWorkspaceId !== null) return;

    hasSeededFromScope.current = true;
    if (currentSelectedScope.scopeType === 'workspace') {
      setPendingWorkspaceId(selectedWorkspaceId);
    }
    // selectedWorkspaceId intentionally read at scope-resolution time, not tracked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedScope, pendingWorkspaceId]);

  // Clear the workspace highlight when the org changes (stale selection).
  useEffect(() => {
    if (previousOrgId.current === null) {
      previousOrgId.current = orgId;
      return;
    }
    if (previousOrgId.current === orgId) {
      return;
    }
    previousOrgId.current = orgId;
    setPendingWorkspaceId(null);
  }, [orgId, setPendingWorkspaceId]);

  // If user manually changes locations away from the pending workspace, clear the highlight.
  // Skip this check if locations still match the workspace (e.g., during Apply flow).
  useEffect(() => {
    if (pendingWorkspaceId === null) return;

    const pendingWorkspaceRow = workspaceRows.find(({ workspace }) => workspace.workspaceId === pendingWorkspaceId);

    if (!pendingWorkspaceRow) {
      setPendingWorkspaceId(null);
      return;
    }

    const sameLocations = areSameLocationSet(pendingWorkspaceRow.intersectedLocationIds, selectedLocationIds);

    // Only clear if locations DON'T match - preserves highlight during Apply
    if (!sameLocations) {
      setPendingWorkspaceId(null);
    }
  }, [selectedLocationIds, pendingWorkspaceId, setPendingWorkspaceId, workspaceRows]);

  const handleWorkspaceClick = useCallback(
    ({ workspace, intersectedLocationIds }: { workspace: Workspace; intersectedLocationIds: string[] }) => {
      setPendingWorkspaceId(workspace.workspaceId);
      onSelectWorkspace(intersectedLocationIds);
    },
    [onSelectWorkspace, setPendingWorkspaceId]
  );

  const handleManageInSettings = useCallback(() => {
    settingsNavigate({ to: '/organization/workspaces' });
  }, [settingsNavigate]);

  useEffect(() => {
    if (!workspacesQuery.hasNextPage || !sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !workspacesQuery.isFetchingNextPage) {
          workspacesQuery.fetchNextPage();
        }
      },
      { rootMargin: '50px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [workspacesQuery.hasNextPage, workspacesQuery.isFetchingNextPage, workspacesQuery.fetchNextPage]);

  if (!workspaceRows.length && !shouldShowLoading) {
    return null;
  }

  if (shouldShowLoading) {
    return (
      <div
        css={css`
          padding: ${theme.spacing(1.5, 2, 2, 2)};
        `}
      >
        <Text size='large' weight='bold' color='default'>
          {t('Workspaces')}
        </Text>
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: ${theme.spacing(1)};
            margin-top: ${theme.spacing(1)};
          `}
        >
          <WorkspaceItemSkeleton />
          <WorkspaceItemSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div
      css={css`
        padding: ${theme.spacing(1.5, 2, 2, 2)};
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: ${theme.spacing(1)};
        `}
      >
        <Text size='large' weight='bold' color='default'>
          {t('Workspaces')}
        </Text>
        {isWorkspaceAdmin && (
          <NakedButton
            onClick={handleManageInSettings}
            css={css`
              display: inline-flex;
              align-items: center;
              gap: ${theme.spacing(0.5)};
              min-height: 32px;
              white-space: nowrap;
              transition: color 0.15s ease;

              &:hover {
                text-decoration: underline;
              }
            `}
          >
            <Icon name='settings-small' size={16} color='primary' />
            <Text size='medium' weight='semibold' color='primary'>
              {t('Manage in Settings')}
            </Text>
          </NakedButton>
        )}
      </div>
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: ${theme.spacing(1)};
          margin-top: ${theme.spacing(1)};
          max-height: 232px; /* 72px * 3 rows + 8px * 2 gaps */
          overflow-y: auto;
          align-content: start;
        `}
      >
        {workspaceRows.map(({ workspace, accessibleCount, intersectedLocationIds, isAccessible }) => {
          const isWorkspaceMatch = pendingWorkspaceId
            ? workspace.workspaceId === pendingWorkspaceId
            : workspace.workspaceId === selectedWorkspaceId;

          // Only highlight if workspace matches AND locations match
          const locationsMatch = areSameLocationSet(intersectedLocationIds, selectedLocationIds);
          const isSelected = isWorkspaceMatch && locationsMatch;

          return (
            <MemoizedWorkspaceItem
              key={workspace.workspaceId}
              workspace={workspace}
              accessibleCount={accessibleCount}
              isSelected={isSelected}
              disabled={!isAccessible}
              onSelect={() => handleWorkspaceClick({ workspace, intersectedLocationIds })}
            />
          );
        })}
        {workspacesQuery.isFetchingNextPage && <WorkspaceItemSkeleton />}
        <div ref={sentinelRef} />
      </div>
    </div>
  );
}
